// Lightweight API client for the backend at api.wiktortylman.space
// Endpoints are based on the public docs at https://api.wiktortylman.space/docs

export interface ApiUser {
  id: string;
  email: string;
  [key: string]: unknown;
}

const DEFAULT_BASE_URL = 'https://api.wiktortylman.space';

const baseUrl = (import.meta.env.VITE_API_BASE_URL as string) || DEFAULT_BASE_URL;

function getAuthHeader(): Record<string, string> {
  const token = localStorage.getItem('token');
  if (!token) {
    console.warn('No authentication token found in localStorage');
    return {};
  }
  console.log('Including auth token in request:', token.substring(0, 10) + '...');
  // Tylko standardowy nagłówek
  return {
    Authorization: `Bearer ${token}`,
  };
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  try {
    console.log(`API Request: ${init?.method || 'GET'} ${baseUrl}${path}`);
    
    const res = await fetch(`${baseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
        ...(init?.headers || {}),
      },
    });

    console.log(`API Response: ${res.status} ${res.statusText} for ${path}`);

    const isJson = res.headers.get('content-type')?.includes('application/json');
    const payload = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      console.error(`API Error ${res.status}:`, {
        path,
        method: init?.method || 'GET',
        status: res.status,
        statusText: res.statusText,
        payload,
        headers: Object.fromEntries(res.headers.entries()),
      });
      
      const message = typeof payload === 'string' ? payload : (payload?.message || payload?.error || 'Request failed');
      throw new Error(`HTTP ${res.status}: ${message}`);
    }
    
    console.log(`API Success: ${path}`, payload);
    return payload as T;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      console.error('Network/CORS error:', {
        path,
        method: init?.method || 'GET',
        error: error.message,
        stack: error.stack,
      });
      const method = init?.method || 'GET';
      throw new Error(`Network error - ${method} method may not be allowed by CORS configuration`);
    }
    
    console.error('API request failed:', {
      path,
      method: init?.method || 'GET',
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
    
    throw error;
  }
}

// Auth
export function apiRegister(email: string, password: string, username: string): Promise<{ token: string }> {
  // Based on OpenAPI spec: POST /auth/register returns 201 Created
  return request<{ token: string }>('/auth/register', {
    method: 'POST',
    body: JSON.stringify({ email, password, username }),
  });
}

export function apiLogin(email: string, password: string): Promise<{ token: string }> {
  // Based on OpenAPI spec: POST /auth/login returns { token: string }
  return request<{ token: string }>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

// Saves - based on actual OpenAPI spec
export interface SaveDocument {
  id: number;
  user_id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

export function listSaves(): Promise<SaveDocument[]> {
  return request<SaveDocument[]>('/saves', {
    method: 'GET',
  });
}

export function createSave(doc: { name: string }): Promise<SaveDocument> {
  return request<SaveDocument>('/saves', {
    method: 'POST',
    body: JSON.stringify(doc),
  });
}

export function updateSave(id: number, doc: { name: string }): Promise<SaveDocument> {
  return request<SaveDocument>(`/saves/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(doc),
  });
}

export function deleteSave(id: number): Promise<void> {
  return request<void>(`/saves/${id}`, {
    method: 'DELETE',
  });
}

// Walnuts - based on actual OpenAPI spec
export interface WalnutEntry {
  id: number;
  save_id: number;
  walnut_id: string;
  found_at: string;
  extra_data?: Record<string, any>;
}

export function getWalnutsForSave(saveId: number): Promise<WalnutEntry[]> {
  return request<WalnutEntry[]>(`/walnuts/${saveId}`, {
    method: 'GET',
  });
}

export function addWalnutToSave(saveId: number, walnutId: string, foundAt?: string): Promise<WalnutEntry> {
  return request<WalnutEntry>(`/walnuts/${saveId}`, {
    method: 'POST',
    body: JSON.stringify({
      walnut_id: walnutId,
      found_at: foundAt || new Date().toISOString(),
    }),
  });
}

export function updateWalnutEntry(saveId: number, id: number, data: { found_at?: string; extra_data?: Record<string, any> }): Promise<WalnutEntry> {
  return request<WalnutEntry>(`/walnuts/${saveId}/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export function deleteWalnutEntry(saveId: number, id: number): Promise<void> {
  return request<void>(`/walnuts/${saveId}/${id}`, {
    method: 'DELETE',
  });
}

// Golden Walnuts - based on actual OpenAPI spec
export interface GoldenWalnutType {
  id: string;
  quantity: number;
  type_of_discovery: string;
  how_to_find: string;
  image: string;
  location: string;
}

export function listGoldenWalnutTypes(): Promise<GoldenWalnutType[]> {
  return request<GoldenWalnutType[]>('/golden-walnuts', {
    method: 'GET',
  });
}


